import React, { useEffect, useState, useRef } from 'react';
import { 
  Container, Box, Typography, Button, Paper, 
  CardContent, Divider, Alert, LinearProgress, Zoom,
  List, ListItemButton, ListItemIcon, Stack
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../api/axiosConfig'; 
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

const getFullVideoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  const CLOUDFRONT_BASE = "https://d1nzw37nurloc4.cloudfront.net";
  return `${CLOUDFRONT_BASE}/${url}`;
};

const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const SecurePlayer = ({ src, height }) => {
  const videoRef = useRef(null);
  const fullSrc = getFullVideoUrl(src);
  if (!fullSrc) return null;

  return (
    <Box sx={{ 
      width: '100%', 
      borderRadius: 2, 
      overflow: 'hidden', 
      bgcolor: '#000', 
      border: '1px solid #333', 
      position: 'relative', 
      boxShadow: 2,
      lineHeight: 0 
    }}>
      <video
        ref={videoRef}
        src={fullSrc}
        controls
        playsInline
        controlsList="nodownload" 
        onContextMenu={(e) => e.preventDefault()} 
        style={{ 
          width: '100%', 
          height: height || 'auto', 
          maxHeight: height || '500px', 
          display: 'block', 
          objectFit: 'contain' 
        }}
      />
    </Box>
  );
};

const StudyPlayer = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  
  const THEME_NAVY = '#1a233a';
  const SUB_NAVY = '#2c3e50';

  const [questions, setQuestions] = useState([]);
  const [historyPk, setHistoryPk] = useState(null); 
  const [currentIndex, setCurrentIndex] = useState(0); 
  const [selectedOptionIndices, setSelectedOptionIndices] = useState([]); 
  const [showAnswer, setShowAnswer] = useState(false); 
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  // 상단으로 이동 (문제 변경 시)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentIndex]);

  // [수정 핵심 부분] 정답 확인 시 브라우저 문서의 아예 맨 밑(바닥)으로 스크롤
  useEffect(() => {
    if (showAnswer) {
      // 해설 렌더링 및 레이아웃 계산 시간을 고려해 setTimeout 사용
      const timer = setTimeout(() => {
        window.scrollTo({
          top: document.documentElement.scrollHeight, // 문서 전체의 높이(맨 바닥)
          behavior: 'smooth'
        });
      }, 150); 
      return () => clearTimeout(timer);
    }
  }, [showAnswer]);

  const enhanceQuestionVideo = async (question) => {
    let data = { 
      ...question, 
      submitted: false,      
      savedOptionIndices: [] 
    };
    
    if (data.sortApplYn === 'N' && data.options && data.options.length > 0) {
      data.options = shuffleArray(data.options);
    }

    if (data.videoPk && !data.videoUrl) {
      try {
        const vRes = await axios.get(`/api/video/${data.videoPk}`);
        data.videoUrl = getFullVideoUrl(vRes.data.videoUrl);
      } catch (err) { console.error("대표 영상 조회 실패:", err); }
    } else {
      data.videoUrl = getFullVideoUrl(data.videoUrl);
    }
    
    if (data.options && data.options.length > 0) {
      data.options = await Promise.all(data.options.map(async (opt) => {
        if (opt.videoPk && !opt.videoUrl) {
          try {
            const ovRes = await axios.get(`/api/video/${opt.videoPk}`);
            return { ...opt, videoUrl: getFullVideoUrl(ovRes.data.videoUrl) };
          } catch { return opt; }
        }
        return { ...opt, videoUrl: getFullVideoUrl(opt.videoUrl) };
      }));
    }
    return data;
  };

  useEffect(() => {
    let isSubscribed = true; 
    const initStudySession = async () => {
      try {
        setLoading(true);
        const [qRes, cListRes] = await Promise.all([
          axios.get(`/api/question`),
          axios.get(`/api/category`)
        ]);

        if (!isSubscribed) return;

        const currentCategory = cListRes.data.find(c => c.categoryPk === Number(categoryId));
        const isSortApplied = currentCategory?.sortApplYn === 'Y';

        let filtered = qRes.data.filter(q => q.categoryPk === Number(categoryId));
        
        if (filtered.length === 0) {
          setQuestions([]);
          setLoading(false);
          return;
        }

        if (!isSortApplied) {
          filtered = shuffleArray(filtered);
        } else {
          filtered.sort((a, b) => a.questionPk - b.questionPk); 
        }

        const hRes = await axios.post('/api/exam/start', { categoryPk: Number(categoryId) });
        if (!isSubscribed) return;
        setHistoryPk(hRes.data); 

        const enhanced = await Promise.all(filtered.map(q => enhanceQuestionVideo(q)));
        
        if (!isSubscribed) return;
        setQuestions(enhanced);
      } catch (err) {
        console.error("세션 초기화 실패:", err);
      } finally {
        if (isSubscribed) setLoading(false);
      }
    };
    initStudySession();
    return () => { isSubscribed = false; };
  }, [categoryId]);

  const handleToggleOption = (index) => {
    if (showAnswer || isProcessing) return;
    
    setSelectedOptionIndices(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const handleCheckAnswer = async () => {
    if (selectedOptionIndices.length === 0 || isProcessing || !historyPk) return;
    const currentQ = questions[currentIndex];
    const correctOptions = currentQ.options.filter(opt => opt.correctYn === 'Y');
    const correctCount = correctOptions.length;

    if (selectedOptionIndices.length !== correctCount) {
      alert(`정답은 ${correctCount}개입니다. 현재 ${selectedOptionIndices.length}개를 선택하셨습니다.`);
      return;
    }

    if (currentQ.submitted) {
      setShowAnswer(true);
      return;
    }

    setIsProcessing(true);
    try {
      const correctOptionIds = correctOptions.map(opt => opt.optionPk);
      const selectedOptionIds = selectedOptionIndices.map(idx => currentQ.options[idx].optionPk);
      const isAllCorrect = selectedOptionIds.every(id => correctOptionIds.includes(id));

      const payload = {
        historyPk: historyPk,
        questionPk: currentQ.questionPk,
        optionPk: currentQ.options[selectedOptionIndices[0]].optionPk, 
        correctYn: isAllCorrect ? 'Y' : 'N'
      };
      
      await axios.post('/api/answer', payload);

      setQuestions(prev => {
        const newArr = [...prev];
        newArr[currentIndex] = { 
          ...newArr[currentIndex], 
          submitted: true, 
          savedOptionIndices: selectedOptionIndices 
        };
        return newArr;
      });

      setShowAnswer(true);
    } catch (err) {
      console.error("기록 저장 실패:", err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFinishExam = async () => {
    if (isProcessing || !historyPk) return;
    setIsProcessing(true);

    try {
      await axios.post(`/api/exam/finish/${historyPk}`);
      const totalScore = questions.reduce((acc, q) => acc + (Number(q.score) || 0), 0);
      
      const earnedScore = questions.reduce((acc, q) => {
        const correctIds = q.options.filter(o => o.correctYn === 'Y').map(o => o.optionPk);
        const selectedIds = (q.savedOptionIndices || []).map(idx => q.options[idx].optionPk);
        const isMatch = correctIds.length === selectedIds.length && selectedIds.every(id => correctIds.includes(id));
        
        return isMatch ? acc + (Number(q.score) || 0) : acc;
      }, 0);

      const correctCount = questions.filter(q => {
        const correctIds = q.options.filter(o => o.correctYn === 'Y').map(o => o.optionPk);
        const selectedIds = (q.savedOptionIndices || []).map(idx => q.options[idx].optionPk);
        return correctIds.length === selectedIds.length && selectedIds.every(id => correctIds.includes(id));
      }).length;

      navigate('/study-result', { 
        state: { 
          totalCount: questions.length,
          correctCount: correctCount,
          categoryName: questions[0]?.categoryName || "수어 학습",
          totalScore: totalScore,
          earnedScore: earnedScore
        } 
      });

    } catch (err) {
      console.error("종료 처리 실패:", err);
      navigate('/category-select');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleNext = () => {
    const nextIndex = currentIndex + 1;
    const nextQ = questions[nextIndex];
    if (nextQ && nextQ.submitted) {
      setSelectedOptionIndices(nextQ.savedOptionIndices || []);
      setShowAnswer(true);
    } else {
      setSelectedOptionIndices([]); 
      setShowAnswer(false);   
    }
    setCurrentIndex(nextIndex);
  };

  const handleBack = () => {
    const prevIndex = currentIndex - 1;
    const prevQ = questions[prevIndex];
    if (prevQ && prevQ.submitted) {
      setSelectedOptionIndices(prevQ.savedOptionIndices || []);
      setShowAnswer(true);
    }
    setCurrentIndex(prevIndex);
  };

  if (loading) return <Box sx={{ width: '100%', mt: 5, px: 3 }}><LinearProgress /></Box>;
  if (questions.length === 0) return (
    <Container sx={{ mt: 10, textAlign: 'center' }}>
      <Typography variant="h6">등록된 문제가 없습니다.</Typography>
      <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>돌아가기</Button>
    </Container>
  );

  const currentQ = questions[currentIndex];
  const totalSteps = questions.length;
  const options = currentQ.options || [];

  const correctOptionIndices = options.map((opt, idx) => opt.correctYn === 'Y' ? idx : null).filter(idx => idx !== null);
  const isCorrect = selectedOptionIndices.length === correctOptionIndices.length && 
                    selectedOptionIndices.every(idx => correctOptionIndices.includes(idx));

  return (
    <Container maxWidth="md" disableGutters sx={{ mt: { xs: 0, sm: 4 }, mb: { xs: 0, sm: 4 }, px: { xs: 0, sm: 2 } }}>
      <Box sx={{ p: { xs: 2, sm: 0 }, mb: { xs: 1, sm: 4 } }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>학습 진행도</Typography>
          <Typography variant="subtitle2" color={THEME_NAVY} sx={{ fontWeight: 'bold' }}>{currentIndex + 1} / {totalSteps}</Typography>
        </Stack>
        <LinearProgress variant="determinate" value={((currentIndex + 1) / totalSteps) * 100} sx={{ height: 10, borderRadius: 5, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: THEME_NAVY } }} />
      </Box>

      <Zoom in={true} key={currentIndex}>
        <Paper elevation={8} sx={{ borderRadius: 4, overflow: 'hidden', bgcolor: '#fcfcfc' }}>
          <Box sx={{ p: { xs: 3, sm: 4 }, bgcolor: THEME_NAVY, color: 'white', position: 'relative', zIndex: 2 }}>
            <Typography variant="overline" sx={{ opacity: 0.8, fontWeight: 'bold', letterSpacing: 1.2 }}>QUESTION {currentIndex + 1}</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.4, mt: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>{currentQ.question}</Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.9 }}>* 정답은 {correctOptionIndices.length}개입니다.</Typography>
          </Box>
          
          <CardContent sx={{ p: { xs: 2, sm: 4 }, bgcolor: '#f8f9fa' }}> 
            {currentQ.videoUrl && <Box sx={{ mb: 4 }}><SecurePlayer src={currentQ.videoUrl} /></Box>}

            <Typography variant="h6" sx={{ mb: 2.5, px: 1, fontWeight: 800, color: THEME_NAVY, fontSize: '1rem', display: 'flex', alignItems: 'center' }}>
              <Box component="span" sx={{ width: 4, height: 18, bgcolor: THEME_NAVY, mr: 1.5, borderRadius: 1 }} />
              정답을 모두 선택하세요
            </Typography>

            <List sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {options.map((option, index) => {
                const isSelected = selectedOptionIndices.includes(index);
                const isCorrectOption = option.correctYn === 'Y';
                let bgColor = isSelected ? '#f0f2f7' : 'white';
                if (showAnswer) {
                  if (isCorrectOption) bgColor = '#e8f5e9'; 
                  else if (isSelected && !isCorrectOption) bgColor = '#ffebee'; 
                }

                return (
                  <ListItemButton 
                    key={index}
                    onClick={() => handleToggleOption(index)}
                    disabled={(showAnswer && !currentQ.submitted) || isProcessing}
                    sx={{ 
                      flexDirection: 'column', alignItems: 'stretch', borderRadius: 3, border: '2px solid',
                      borderColor: isSelected ? THEME_NAVY : '#eaedf0', 
                      bgcolor: bgColor, p: { xs: 2, sm: 2.5 }, 
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: option.videoUrl ? 2 : 0 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        {isSelected ? <CheckCircleOutlineIcon sx={{ color: THEME_NAVY }} /> : <RadioButtonUncheckedIcon color="disabled" />}
                      </ListItemIcon>
                      <Typography sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' }, fontWeight: isSelected ? 700 : 500, flexGrow: 1, color: SUB_NAVY }}>
                        {option.optionText}
                      </Typography>
                    </Stack>
                    {option.videoUrl && <Box sx={{ width: '100%', mt: 1 }}><SecurePlayer src={option.videoUrl} /></Box>}
                  </ListItemButton>
                );
              })}
            </List>

            {showAnswer && (
              <Box sx={{ mt: 5, px: 0.5 }}>
                <Divider sx={{ mb: 3 }} />
                <Alert severity={isCorrect ? "success" : "error"} sx={{ mb: 3, borderRadius: 2, fontWeight: 'bold', fontSize: '1rem' }}>
                  {isCorrect ? "훌륭합니다! 모든 정답을 맞추셨습니다." : "아쉽네요, 정답을 다시 한번 확인해보세요."}
                </Alert>
                <Box sx={{ p: 2.5, bgcolor: 'white', borderRadius: 3, borderLeft: `6px solid ${THEME_NAVY}` }}>
                  <Typography variant="subtitle1" sx={{ color: THEME_NAVY, mb: 1, fontWeight: 900, display: 'flex', alignItems: 'center' }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: '1.2rem', mr: 1 }} /> 정답 해설
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, color: '#4a5568' }}>
                    {currentQ.description || "해설이 등록되어 있지 않습니다."}
                  </Typography>
                </Box>
              </Box>
            )}
          </CardContent>

          <Box sx={{ p: 2.5, bgcolor: '#ffffff', display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #edf2f7' }}>
            <Button onClick={handleBack} disabled={currentIndex === 0 || isProcessing} startIcon={<KeyboardArrowLeft />} sx={{ color: THEME_NAVY, fontWeight: 700 }}>이전</Button>
            {!showAnswer ? (
              <Button 
                variant="contained" 
                disabled={selectedOptionIndices.length === 0 || isProcessing} 
                onClick={handleCheckAnswer} 
                sx={{ bgcolor: THEME_NAVY, px: 5, fontWeight: 700, borderRadius: 2, '&:hover': { bgcolor: SUB_NAVY } }}
              >
                {isProcessing ? "저장 중..." : "정답 확인"}
              </Button>
            ) : (
              <Button 
                variant="contained" 
                disabled={isProcessing}
                onClick={currentIndex === totalSteps - 1 ? handleFinishExam : handleNext} 
                color={currentIndex === totalSteps - 1 ? "success" : "primary"}
                sx={{ px: 5, fontWeight: 700, borderRadius: 2 }}
              >
                {currentIndex === totalSteps - 1 ? "학습 완료" : "다음 문제"}
              </Button>
            )}
          </Box>
        </Paper>
      </Zoom>
    </Container>
  );
};

export default StudyPlayer;